// Encapsulates fixed-case best-deal shortcuts to keep main algorithms clean.
import { replaceWithParentTests } from ".";
import {
  DBS_FIXED_IDS,
  DBS_ANY_THREE_BUNDLE_ID,
  DBS_ANY_FIVE_BUNDLE_ID,
  ANY_THREE_DEFAULT_PRICE,
  ANY_FIVE_DEFAULT_PRICE,
  DBS_FIXED_ANY_THREE_IDS,
  DBS_FIXED_ANY_FIVE_CANDIDATES,
  HEAVY_METAL_TEST_ID,
  HEAVY_METAL_SPECIAL_PRICE,
} from "./bestDealConstants";

/**
 * If newValueIds exactly matches DBS_FIXED_IDS, return a predefined Any3 + Any5 combo result.
 * Otherwise, return null so caller can continue with general algorithm.
 */
export function tryBuildFixedDbsCombo(
  allSuggestions,
  newValueIds,
  vaTestList,
  vwTestList,
  testNodeList,
  isHeavyMetalSpecialPrice
) {
  const fixedIds = DBS_FIXED_IDS;
  const sortedInput = [...newValueIds].sort((a, b) => a - b);
  const sortedFixed = [...fixedIds].sort((a, b) => a - b);
  const isExactMatch =
    sortedInput.length === sortedFixed.length &&
    sortedInput.every((v, i) => v === sortedFixed[i]);
  if (!isExactMatch) return null;

  const threeIds = DBS_FIXED_ANY_THREE_IDS;
  const fiveIdsRaw = DBS_FIXED_ANY_FIVE_CANDIDATES;
  const fiveIdsParented = replaceWithParentTests(fiveIdsRaw, testNodeList).slice(0, 5);

  const usedIds = new Set([...threeIds, ...fiveIdsRaw]);
  const remainingNewValues = newValueIds.filter((id) => !usedIds.has(id));

  const anyThreePromo = allSuggestions.find((i) => i.bundleId === DBS_ANY_THREE_BUNDLE_ID);
  const anyFivePromo = allSuggestions.find((i) => i.bundleId === DBS_ANY_FIVE_BUNDLE_ID);

  const any3Price = anyThreePromo ? anyThreePromo.finalPrice : ANY_THREE_DEFAULT_PRICE;
  const any5Price = anyFivePromo ? anyFivePromo.finalPrice : ANY_FIVE_DEFAULT_PRICE;

  const leftoverPrice = remainingNewValues
    .map((id) => vaTestList.concat(vwTestList).find((t) => t.id === id))
    .filter(Boolean)
    .reduce(
      (sum, t) => sum + (isHeavyMetalSpecialPrice && t.id === HEAVY_METAL_TEST_ID ? HEAVY_METAL_SPECIAL_PRICE : t.price),
      0
    );

  return {
    appliedBundles: [
      {
        appliedBundleId: anyThreePromo ? anyThreePromo.bundleId : DBS_ANY_THREE_BUNDLE_ID,
        appliedZoomers: threeIds,
        appliedSupplement: [],
        bundlePrice: any3Price,
      },
      {
        appliedBundleId: anyFivePromo ? anyFivePromo.bundleId : DBS_ANY_FIVE_BUNDLE_ID,
        appliedZoomers: fiveIdsParented,
        appliedSupplement: [],
        bundlePrice: any5Price,
      },
    ],
    remainingNewValues,
    priceAfterAppliedBundle: (any3Price + any5Price + leftoverPrice).toFixed(2),
    bundlesKey: `${anyThreePromo ? anyThreePromo.bundleId : DBS_ANY_THREE_BUNDLE_ID},${
      anyFivePromo ? anyFivePromo.bundleId : DBS_ANY_FIVE_BUNDLE_ID
    }`,
  };
}
